import React, { useState, useEffect } from 'react';
import {
  Box,
  Input,
  InputGroup,
  InputLeftAddon,
  useColorModeValue,
} from '@chakra-ui/react';
import { Typography } from '@/kit/antipad-ui';

interface CurrencyFilterInputProps {
  initialValue: number;
  onChange: (value: number) => void;
}

export const CurrencyFilterInput: React.FC<CurrencyFilterInputProps> = ({
  initialValue,
  onChange
}) => {
  const [inputValue, setInputValue] = useState('');
  const [isFocused, setIsFocused] = useState(false);

  // Форматируем значение для отображения в поле ввода
  const formatAsCurrency = (value: number): string => {
    // Если значение равно 0, возвращаем пустую строку
    if (value === 0) return '';

    // Форматируем число как валюту
    return value.toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    });
  };

  // Парсим отформатированное значение обратно в число
  const parseCurrencyValue = (formattedValue: string): number => {
    // Удаляем все нечисловые символы кроме точки и запятой
    let numericValue = formattedValue.replace(/[^0-9.,]/g, '');

    // Заменяем запятую на точку для правильной работы parseFloat
    numericValue = numericValue.replace(',', '.');

    // Ограничиваем ввод максимум 2 знаками после точки
    if (numericValue.includes('.')) {
      const parts = numericValue.split('.');
      if (parts[1]?.length > 2) {
        numericValue = `${parts[0]}.${parts[1].slice(0, 2)}`;
      }
    }

    return numericValue ? parseFloat(numericValue) : 0;
  };

  useEffect(() => {
    // Инициализируем отформатированное значение при загрузке
    setInputValue(formatAsCurrency(initialValue));
  }, [initialValue]);

  const handleValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value;

    // Если мы в режиме фокуса, разрешаем ввод только чисел, точек и запятых
    if (isFocused) {
      // Убираем знак доллара, если он есть
      const valueWithoutCurrency = rawValue.replace('$', '');

      // Проверяем, содержит ли ввод только допустимые символы
      if (!/^[0-9]*[.,]?[0-9]*$/.test(valueWithoutCurrency) && valueWithoutCurrency !== '') {
        return;
      }

      setInputValue(valueWithoutCurrency);

      // Парсим значение и оповещаем родителя
      const parsedValue = parseCurrencyValue(valueWithoutCurrency);
      onChange(parsedValue);
    } else {
      // Если не в режиме фокуса, просто сохраняем сырое значение
      setInputValue(rawValue);
    }
  };

  const handleInputFocus = () => {
    setIsFocused(true);
    // При фокусе показываем неформатированное значение для удобного редактирования
    setInputValue(initialValue.toString());
  };

  const handleInputBlur = () => {
    setIsFocused(false);
    // При потере фокуса форматируем значение
    setInputValue(formatAsCurrency(initialValue));
  };

  return (
    <InputGroup maxW="310px" borderRadius="3xl" >
      <InputLeftAddon
        bg="#191919"
        color="white"
        fontWeight="medium"
        borderLeftRadius="3xl"
      // borderRightRadius="3xl"
      >
        <Typography>
          Ignore
        </Typography>
      </InputLeftAddon>
      <Input
        type="text"
        inputMode="numeric"
        value={isFocused ? inputValue : `$${inputValue}`}
        onChange={handleValueChange}
        onFocus={handleInputFocus}
        onBlur={handleInputBlur}
        // size="sm"
        style={{ font: 'var(--typo-body-regular)', outline: 'none', border: 'none' }}
        bg="var(--color-background-neutral-subtle)"
        placeholder="$10"
        borderLeftRadius="3xl"
        borderRightRadius="3xl"
        borderColor="transparent"

        color="white"
        pr={3}
      />
    </InputGroup>
  );
}; 